import { useDarkMode } from '@/contexts/DarkModeContext';
import Alert from 'antd/es/alert';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import Typography from 'antd/es/typography';
import React, { useState } from 'react';
import { styled } from 'styled-components';
import { JsonEditor } from 'ui-components';

import type { LLMBlockConfig, PipelineBlock } from '@mai/types';

import { EnhancedNumberInput, EnumSelect } from './shared';

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.marginXS}px;
`;

const EditorLabel = styled(Typography.Text)`
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.marginXXS}px;
`;

const EditorHelp = styled(Typography.Text)`
  color: ${({ theme }) => theme.colorTextSecondary};
  font-size: ${({ theme }) => theme.fontSizeSM}px;
  margin-bottom: ${({ theme }) => theme.marginXS}px;
`;

interface LLMBlockEditorProps {
  block: PipelineBlock & { type: 'llm' };
  onConfigChange: (config: LLMBlockConfig) => void;
}

export const LLMBlockEditor: React.FC<LLMBlockEditorProps> = ({
  block,
  onConfigChange,
}) => {
  const [form] = Form.useForm();
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [isDarkMode] = useDarkMode();

  // Initialize form with current values
  React.useEffect(() => {
    form.setFieldsValue({
      provider: block.config.provider || 'anthropic',
      model: block.config.model || 'small',
      model_override: block.config.model_override || '',
      temperature: block.config.temperature ?? 0.2,
      max_tokens: block.config.max_tokens || 8192,
    });
  }, [block.config, form]);

  // Convert the current output_schema to a string for the editor with prettification
  const currentOutputSchemaString = React.useMemo(() => {
    const schema = block.config.output_schema;

    if (!schema) {
      return '{}';
    }

    try {
      let jsonToStringify;

      if (typeof schema === 'string') {
        if ((schema as string).trim() === '') {
          return '{}';
        }
        jsonToStringify = JSON.parse(schema);
      } else {
        jsonToStringify = schema;
      }

      if (
        typeof jsonToStringify === 'object' &&
        Object.keys(jsonToStringify).length === 0
      ) {
        return '{}';
      }

      return JSON.stringify(jsonToStringify, null, 2);
    } catch {
      if (typeof schema === 'string') {
        return schema;
      }
      return '{}';
    }
  }, [block.config.output_schema]);

  const handleValuesChange = (
    _: unknown,
    allValues: Partial<LLMBlockConfig>,
  ) => {
    onConfigChange({
      ...block.config,
      ...allValues,
    });
  };

  const handleOutputSchemaChange = React.useCallback(
    (value: string) => {
      if (isValid) {
        try {
          const parsedSchema = JSON.parse(value);
          onConfigChange({
            ...block.config,
            output_schema: parsedSchema,
          });
        } catch {
          // Invalid JSON - don't update config
        }
      }
    },
    [block.config, onConfigChange, isValid],
  );

  const handleValidation = React.useCallback(
    (valid: boolean, errorMessage?: string) => {
      setIsValid(valid);
      setError(errorMessage || null);
    },
    [],
  );

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        size="small"
        onValuesChange={handleValuesChange}
      >
        <EnumSelect
          label="Provider"
          name="provider"
          options={[
            { value: 'anthropic', label: 'Anthropic Claude' },
            { value: 'openai', label: 'OpenAI GPT' },
            { value: 'cerebras', label: 'Cerebras' },
            { value: 'cohere', label: 'Cohere' },
            { value: 'groq', label: 'Groq' },
            { value: 'google', label: 'Google Gemini' },
            { value: 'vertexai', label: 'Google Vertex AI' },
          ]}
          placeholder="anthropic"
          help="The AI provider to use"
        />

        <EnumSelect
          label="Model"
          name="model"
          options={[
            { value: 'small', label: 'Small' },
            { value: 'medium', label: 'Medium' },
            { value: 'large', label: 'Large' },
          ]}
          placeholder="small"
          help="Model size (small, medium, large)"
        />

        <Form.Item
          label="Model Override"
          name="model_override"
          help="Specific model identifier to override the size setting"
        >
          <Input placeholder="claude-3-haiku-20240307" />
        </Form.Item>

        <EnhancedNumberInput
          label="Temperature"
          name="temperature"
          min={0}
          max={2}
          step={0.1}
          placeholder="0.2"
          help="Randomness in responses (0 = deterministic, 2 = very random)"
          useSlider
          sliderMarks={[
            { value: 0, label: 'Conservative' },
            { value: 1, label: 'Balanced' },
            { value: 2, label: 'Creative' },
          ]}
        />

        <EnhancedNumberInput
          label="Max Tokens"
          name="max_tokens"
          min={1}
          max={128000}
          placeholder="8192"
          help="Maximum number of tokens in the response"
          quickValues={[
            { value: 1024, label: '1K' },
            { value: 2048, label: '2K' },
            { value: 4096, label: '4K' },
            { value: 8192, label: '8K' },
            { value: 16384, label: '16K' },
          ]}
        />
      </Form>

      <EditorContainer>
        <EditorLabel>Output Schema (Optional)</EditorLabel>
        <EditorHelp>
          JSON schema to validate and structure the LLM response
        </EditorHelp>

        {error && (
          <Alert
            type="error"
            message="Invalid JSON"
            description={error}
            showIcon
          />
        )}

        <JsonEditor
          value={currentOutputSchemaString}
          onChange={handleOutputSchemaChange}
          onValidate={handleValidation}
          height="200px"
          placeholder='{"type": "object", "properties": {"answer": {"type": "string"}, "confidence": {"type": "number"}}}'
          theme={isDarkMode ? 'dark' : 'light'}
        />
      </EditorContainer>
    </>
  );
};
